import { useEffect, useRef, useState } from "react";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { renderAsync } from "docx-preview";

const RENDER_OPTIONS = {
  inWrapper: true,
  breakPages: true,
  ignoreWidth: false,
  ignoreHeight: false,
  ignoreFonts: false,
  useBase64URL: true,
  renderHeaders: true,
  renderFooters: true,
  renderFootnotes: true,
  renderEndnotes: true,
};

type Props = {
  documentBlob: Blob | null;
  loading: boolean;
  error: string | null;
};

export function ManualSistemaIntegradoViewer({ documentBlob, loading, error }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.replaceChildren();
    setRenderError(null);

    if (!documentBlob) {
      setRendering(false);
      return;
    }

    let disposed = false;
    setRendering(true);

    void renderAsync(documentBlob, container, undefined, RENDER_OPTIONS)
      .catch(() => {
        if (!disposed) {
          setRenderError(
            "El navegador no pudo representar este documento. El archivo Word sigue disponible para descargar.",
          );
        }
      })
      .finally(() => {
        if (!disposed) setRendering(false);
      });

    return () => {
      disposed = true;
      container.replaceChildren();
    };
  }, [documentBlob]);

  if (loading) {
    return (
      <div className="iso27001-feedback-state manual-si-feedback-state">
        <Loader2 size={24} className="animate-spin" />
        <div className="iso27001-feedback-title">Cargando el Manual del Sistema Integrado</div>
        <p>El documento puede tardar unos segundos por su tamaño.</p>
      </div>
    );
  }

  if (error || renderError) {
    return (
      <div className="iso27001-feedback-state manual-si-feedback-state manual-si-feedback-error">
        <AlertTriangle size={24} />
        <div className="iso27001-feedback-title">No se pudo mostrar la previsualización</div>
        <p>{renderError ?? error}</p>
      </div>
    );
  }

  if (!documentBlob) {
    return (
      <div className="iso27001-feedback-state manual-si-feedback-state">
        <FileText size={26} />
        <div className="iso27001-feedback-title">Todavía no se ha cargado el manual</div>
        <p>Un usuario autorizado puede subir el archivo DOCX inicial.</p>
      </div>
    );
  }

  return (
    <div className="manual-si-viewer-shell">
      {rendering ? (
        <div className="manual-si-rendering-indicator">
          <Loader2 size={16} className="animate-spin" />
          Preparando el documento…
        </div>
      ) : null}
      <div ref={containerRef} className="manual-si-document" aria-label="Previsualización del manual" />
    </div>
  );
}
